import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useAuth, useCompany } from '../hooks/useAuth';
import { AppColors } from '../theme/colors';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';
import { CancellationService } from '../services/cancellationService';
import { useIsDesktop } from '../hooks/useMediaQuery';

export const CancellationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();

  const [agreed, setAgreed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isSuperAdmin = user?.userType === 'superadmin';

  const scheduledInfo = useMemo(() => {
    if (!company) return null;
    const status = (company as any).cancellationStatus as string | undefined;
    if (status !== 'scheduled') return null;
    return {
      effectiveAt: (company as any).cancellationEffectiveAt,
      requestedAt: (company as any).cancellationRequestedAt,
    };
  }, [company]);

  const handleSubmit = async () => {
    if (!company?.id || !isSuperAdmin || !user?.uid) return;
    setSubmitting(true);
    setMessage(null);
    try {
      await CancellationService.request(company.id, user.uid);
      setMessage({ type: 'success', text: '退会を申請しました。月末までは引き続きご利用いただけます。' });
      setConfirmOpen(false);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '退会申請に失敗しました。しばらくしてから再度お試しください。' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!company?.id || !isSuperAdmin || !user?.uid) return;
    setSubmitting(true);
    setMessage(null);
    try {
      await CancellationService.revoke(company.id, user.uid);
      setMessage({ type: 'success', text: '退会申請を取り消しました。' });
      setCancelConfirmOpen(false);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '取り消しに失敗しました。しばらくしてから再度お試しください。' });
    } finally {
      setSubmitting(false);
    }
  };

  const formattedDate = (d: any) => {
    try {
      const date = d?.toDate ? d.toDate() : new Date(d);
      return date.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch (_) {
      return '-';
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>このページの閲覧にはシステム管理者権限が必要です。</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/admin')}>管理者画面へ戻る</Button>
      </Box>
    );
  }

  return (
    <ResponsiveAdminLayout pageTitle="退会処理">
      <Box>

        {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
        <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%' }}>
        {/* コンテンツエリア */}
        <Box sx={{ p: 2 }}>

        {/* 退会の影響について */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 2, border: `1px solid ${AppColors.border}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black', mb: 2 }}>退会の影響について</Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, display: 'inline' }}>注意</Typography>
            <Typography variant="body2" sx={{ display: 'inline', ml: 2 }}>
              退会処理を行うと、以下の影響があります。必ずご確認ください。
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <CheckCircleOutlineIcon sx={{ color: '#2e7d32', mt: '2px' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>当月末まで利用可能</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>退会申請後も、当月末まで通常通りサービスをご利用いただけます。</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <HighlightOffIcon sx={{ color: '#d32f2f', mt: '2px' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>月末終了後、自動的にサービス停止</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>月末終了後は、全ての機能へのアクセスが制限されます。</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <HighlightOffIcon sx={{ color: '#d32f2f', mt: '2px' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>データの復元はできません。</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>再度のご利用には、新たにご契約いただく必要があります。</Typography>
            </Box>
          </Box>
        </Card>

        {/* 退会対象の会社情報 */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 2, border: `1px solid ${AppColors.border}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black', mb: 2 }}>退会対象の会社情報</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            会社名: {company?.companyName || '-'}
          </Typography>
          {/* 現在プラン表示は一旦非表示（単一プラン運用のためコメントアウト） */}
          {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            現在のプラン: {(company as any)?.currentPlan || '-'}
          </Typography> */}
        </Card>

        {scheduledInfo && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            退会が予約されています。利用可能期限: {formattedDate(scheduledInfo.effectiveAt)}
          </Alert>
        )}

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>
        )}

        <Card sx={{ p: 2.5, borderRadius: 3, mb: 2, border: `1px solid ${AppColors.border}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black', mb: 2 }}>注意事項</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', mb: 2 }}>
            {`・退会申請後も当月末まではご利用いただけます。
・当月末を過ぎるとご利用いただけなくなります。
・再度のご利用には、新たにご契約いただく必要があります。`}
          </Typography>

          {!scheduledInfo && (
            <>
              <FormControlLabel
                control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
                label="注意事項に同意します"
              />

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={!agreed || submitting}
                  onClick={() => setConfirmOpen(true)}
                >
                  退会する
                </Button>
              </Box>
            </>
          )}
        </Card>

        {scheduledInfo && (
          <Card sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="outlined" color="primary" onClick={() => setCancelConfirmOpen(true)} disabled={submitting}>
                退会申請を取り消す
              </Button>
            </Box>
          </Card>
        )}
      </Box>
      </Box>

      {/* 最終確認ダイアログ */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>本当に退会しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText>
            当月末までは引き続きご利用いただけます。退会を申請しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>キャンセル</Button>
          <Button onClick={handleSubmit} disabled={submitting} color="error" variant="contained">退会する</Button>
        </DialogActions>
      </Dialog>

      {/* 取り消し確認ダイアログ */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)}>
        <DialogTitle>退会申請を取り消しますか？</DialogTitle>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>キャンセル</Button>
          <Button onClick={handleCancelReservation} disabled={submitting} variant="contained">取り消す</Button>
        </DialogActions>
      </Dialog>
        </Box>
    </ResponsiveAdminLayout>
  );
};

export default CancellationPage;


